import {CssBaseline, ThemeProvider} from "@mui/material";
import {type PaletteMode} from "@mui/material/styles";
import {createContext, type ReactNode, useContext, useEffect, useMemo, useState} from "react";
import {getAppTheme} from "./appTheme";

const THEME_MODE_STORAGE_KEY = "themeMode";

type ThemeModeContextType = {
    themeMode: PaletteMode;
    setThemeMode: (mode: PaletteMode) => void;
};

const ThemeModeContext = createContext<ThemeModeContextType>({
    themeMode: "light",
    setThemeMode: () => undefined,
});

const getStoredThemeMode = (): PaletteMode => {
    const storedThemeMode = window.localStorage.getItem(THEME_MODE_STORAGE_KEY);
    return storedThemeMode === "dark" ? "dark" : "light";
};

export const useThemeMode = () => useContext(ThemeModeContext);

export const AppThemeProvider = ({children}: {children: ReactNode}) => {
    const [themeMode, setThemeMode] = useState<PaletteMode>(getStoredThemeMode);

    useEffect(() => {
        window.localStorage.setItem(THEME_MODE_STORAGE_KEY, themeMode);
    }, [themeMode]);

    const theme = useMemo(() => getAppTheme(themeMode), [themeMode]);
    const value = useMemo(
        () => ({
            themeMode,
            setThemeMode,
        }),
        [themeMode],
    );

    return (
        <ThemeModeContext.Provider value={value}>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                {children}
            </ThemeProvider>
        </ThemeModeContext.Provider>
    );
};
